import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, initializeCollections, initializeNotifications } from '../lib/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  needsTermsAcceptance: boolean;
  acceptTerms: () => Promise<void>;
  declineTerms: () => Promise<void>;
  signOut: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  loading: true,
  signIn: async () => {},
  needsTermsAcceptance: false,
  acceptTerms: async () => {},
  declineTerms: async () => {},
  signOut: async () => {},
  resendVerificationEmail: async () => {}
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        setLoading(true);
        if (!currentUser) {
          setUser(null);
          setNeedsTermsAcceptance(false);
          setLoading(false);
          return;
        }

        // Check email verification
        if (!currentUser.emailVerified) {
          await auth.signOut();
          setUser(null);
          setError('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.');
          setLoading(false);
          return;
        }

        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        if (!userData) {
          await auth.signOut();
          setUser(null);
          setLoading(false);
          return;
        }

        // Update last active timestamp
        await updateDoc(userRef, {
          lastActiveAt: serverTimestamp(),
          lastLoginAt: userData.lastLoginAt || serverTimestamp(),
          emailVerified: currentUser.emailVerified
        });

        // Special handling for managers
        if (userData.role === 'manager') {
          if (!userData.approved) {
            console.log('Manager account not approved');
            await auth.signOut();
            setUser(null);
            setLoading(false);
            return;
          }
        } else if (!userData.approved) {
          const settingsDoc = await getDoc(doc(db, 'settings', 'userPermissions'));
          const settings = settingsDoc.data();
          
          if (!settings?.autoApproveUsers) {
            await auth.signOut();
            setUser(null);
            setLoading(false);
            return;
          } else {
            await updateDoc(userRef, {
              approved: true,
              approvedAt: serverTimestamp()
            });
            userData.approved = true;
          }
        }

        // Check terms acceptance
        if (userData.approved && !userData.termsAccepted) {
          setNeedsTermsAcceptance(true);
        }

        // Initialize features for approved managers
        if (userData.role === 'manager' && userData.approved) {
          await initializeCollections();
        }

        // Initialize notifications for approved users
        if (userData.approved && userData.termsAccepted) {
          await initializeNotifications(currentUser.uid);
        }

        setUser(currentUser);
        setLoading(false);
      } catch (error) {
        console.error('Error in auth state change:', error);
        if (user) {
          await auth.signOut();
        }
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string, rememberMe: boolean) => {
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      
      const { user: currentUser } = await signInWithEmailAndPassword(auth, email, password);

      if (!currentUser.emailVerified) {
        await auth.signOut();
        throw new Error('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.');
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      if (!userData) {
        await auth.signOut();
        throw new Error('Benutzerkonto nicht gefunden.');
      }

      // Special handling for managers
      if (userData.role === 'manager') {
        if (!userData.approved) {
          await auth.signOut();
          throw new Error('Ihr Konto muss noch von einem Administrator freigeschaltet werden.');
        }
      } else if (!userData.approved) {
        const settingsDoc = await getDoc(doc(db, 'settings', 'userPermissions'));
        const settings = settingsDoc.data();

        if (!settings?.autoApproveUsers) {
          await auth.signOut();
          throw new Error('Ihr Konto muss noch von einem Verwalter freigeschaltet werden.');
        }
      }

      // Update login timestamp and remember me preference
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        rememberMe,
        lastActiveAt: serverTimestamp()
      });

      if (!userData.termsAccepted) {
        setNeedsTermsAcceptance(true);
      }

      // Set user state immediately after successful login
      setUser(currentUser);
      
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) {
      throw new Error('Kein Benutzer angemeldet');
    }
    await sendEmailVerification(auth.currentUser);
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          lastActiveAt: serverTimestamp()
        });
      }
      
      await auth.signOut();
      setUser(null);
      setNeedsTermsAcceptance(false);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const acceptTerms = async () => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        termsAccepted: true,
        termsAcceptedAt: serverTimestamp()
      });
      setNeedsTermsAcceptance(false);
      await initializeNotifications(user.uid);
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  };

  const declineTerms = async () => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        approved: false,
        termsDeclinedAt: serverTimestamp()
      });
      await handleSignOut();
    } catch (error) {
      console.error('Error declining terms:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      signIn,
      error,
      needsTermsAcceptance,
      acceptTerms,
      declineTerms,
      signOut: handleSignOut,
      resendVerificationEmail
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
